import app from 'flarum/forum/app';
import m from 'mithril';
import { override } from 'flarum/common/extend';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import LogInModal from 'flarum/forum/components/LogInModal';
import { currentNotice, isGuest, isPageLocked, isDiscussionPath, maybeShowGuestNotice } from '../utils/guestGuard';
import { markLandingSeen } from '../utils/landingState';
import navigateTo from '../utils/navigate';

/*
 * 帖子详情页的「游客锁定视图」。
 *
 * ============================================================
 * 为什么要改（1.7.0）
 * ============================================================
 * 1.6.x 的做法是：服务端把游客从 /d/xxx 302 回首页 —— 结果「整个页面」
 *    都被换掉了（列表没了、位置丢了），体验很差。
 *
 * 现在改成：
 *   - 游客访问 /d/123 → 页面**停在原地**（地址栏仍是 /d/123），
 *     只是把正文区换成一张「内容已锁定」的卡片，另外弹一次提示窗；
 *   - 卡片上给「登录 / 注册」和「返回首页」两个出口；
 *   - 卡片里登录成功后，不需要刷新：检测到已登录会自动补一次加载，
 *     直接变成正常的帖子页。
 *
 * 正文数据本身不会下发：服务端 HideGuestContent 中间件会把游客页面
 * HTML 里的 apiDocument 剥掉，前端这里也不发起任何 API 请求。
 */

function goHome() {
  navigateTo('/');
}

function goLogin() {
  try {
    app.modal.show(LogInModal);
  } catch (e) {
    navigateTo('/');
  }
}

function lockView() {
  // 游客给「登录 / 注册」；已登录但没绑定班级的人只需要回首页
  const showLogin = isGuest();

  return (
    <div className="AcmeGuestLock">
      <div className="AcmeGuestLock-card">
        <h3 className="AcmeGuestLock-title">暂时无法查看帖子内容</h3>
        <p className="AcmeGuestLock-text">{currentNotice()}</p>
        <div className="AcmeGuestLock-actions">
          {showLogin ? (
            <button type="button" className="Button Button--primary AcmeGuestLock-login" onclick={goLogin}>
              登录 / 注册
            </button>
          ) : null}
          <button type="button" className="Button AcmeGuestLock-home" onclick={goHome}>
            返回首页
          </button>
        </div>
      </div>
    </div>
  );
}

/*
 * SPA 内点进去的情况：没有服务端渲染，body 上没有 AcmeContentLocked 标记，
 * 帖子数据只能靠 XHR 拉 —— 无权时后端 RestrictContentAccess 直接返回 403。
 *
 * Flarum 遇到 403 只会弹一句「出错了」，页面会空着。这里兜底：
 * 加载一段时间之后 this.discussion 仍然是空的，就认定是被拦了，
 * 换成锁定卡片（加载成功后 this.discussion 有值，会自然恢复正常视图）。
 */
function scheduleLockCheck(page) {
  const check = (attempt) => {
    try {
      // 已经离开帖子页了，不必再判断
      if (!isDiscussionPath(m.route.get())) return;

      if (page.discussion) {
        page.__acmeLoadFailed = false;
        return;
      }

      if (attempt < 2) {
        setTimeout(() => check(attempt + 1), 800);
        return;
      }

      page.__acmeLoadFailed = true;
      markLandingSeen();
      maybeShowGuestNotice();

      try {
        m.redraw();
      } catch (e) {
        // ignore
      }
    } catch (e) {
      // ignore
    }
  };

  setTimeout(() => check(0), 900);
}

export default function installDiscussionLock() {
  override(DiscussionPage.prototype, 'oninit', function (orig, vnode) {
    this.__acmeOrigOninit = typeof orig === 'function' ? orig : null;
    this.__acmeVnode = vnode;

    if (isPageLocked()) {
      // 游客：不加载正文，也就不会触发被后端拦掉的 API 请求
      this.__acmeSkipped = true;
      return;
    }

    if (this.__acmeOrigOninit) this.__acmeOrigOninit.call(this, vnode);

    scheduleLockCheck(this);
  });

  override(DiscussionPage.prototype, 'oncreate', function (orig, vnode) {
    if (isPageLocked()) {
      // 已经在看帖子了，手机端整屏着陆页没必要再出现
      markLandingSeen();
      maybeShowGuestNotice();
      return;
    }

    if (orig) orig.call(this, vnode);
  });

  override(DiscussionPage.prototype, 'view', function (orig) {
    if (isPageLocked()) {
      return lockView();
    }

    // 上面的延时检查判定为被后端拦下（API 403）→ 同样给锁定卡片
    if (this.__acmeLoadFailed && !this.discussion) {
      return lockView();
    }

    /*
     * 游客先打开帖子页（oninit 被跳过），随后在站内完成登录的情况：
     * 这里补一次原始的 oninit，把帖子重新加载出来，用户不用手动刷新。
     */
    if (this.__acmeSkipped && !this.discussion && this.__acmeOrigOninit && !this.__acmeReloading) {
      this.__acmeReloading = true;
      setTimeout(() => {
        try {
          this.__acmeOrigOninit.call(this, this.__acmeVnode || {});
        } catch (e) {
          // ignore
        }
        m.redraw();
      }, 0);
    }

    return orig();
  });
}
